import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { featuredSitters, citiesByCountry, services } from '../../mocks/sitters';
import ReviewsSection from './components/ReviewsSection';
import StatsSection from './components/StatsSection';
import SitterRecruitSection from './components/SitterRecruitSection';
import FAQSection from './components/FAQSection';

export default function HomePage() {
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedCity) params.set('city', selectedCity);
    if (selectedService) params.set('service', selectedService);
    navigate(`/sitters${params.toString() ? '?' + params.toString() : ''}`);
  };

  return (
    <div className="min-h-screen bg-[#EEF4F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#81D8D0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-24 sm:h-28">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg" 
                alt="PawBridge" 
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
              />
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              <Link to="/about" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                PawBridgeについて
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitters" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                シッターを探す
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitter-registration" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                シッター登録
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/login"
                className="px-6 py-2.5 bg-white text-[#81D8D0] text-base font-bold rounded-full hover:bg-[#1B2A4A] hover:text-white transition-all shadow-md hover:shadow-lg whitespace-nowrap"
              >
                ログイン
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-1 animate-fadeIn border-t border-white/20 pt-3">
              <Link 
                to="/about" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                PawBridgeについて
              </Link>
              <Link 
                to="/sitters" 
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                シッターを探す
              </Link>
              <Link 
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                PawBridgeについて
              </Link>
              <Link 
                to="/sitter-registration"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                シッター登録
              </Link>
              <Link 
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                ログイン
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-28 min-h-[90vh] flex items-center overflow-hidden">
        {/* 背景イラスト（全体に広がる） */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/10c06283ec2712a2d97fc63fcb0a7bf4.svg"
            alt="PawBridge ペットシッター"
            className="w-full h-full object-contain object-right-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#EEF4F7]/70 via-[#EEF4F7]/50 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 w-full">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B2A4A] leading-tight mb-4 sm:mb-6">
              海外でも、<br />
              愛犬を安心して<br />
              任せられる。
            </h1>
            <p className="text-base sm:text-lg text-[#1B2A4A]/70 mb-6 sm:mb-8 md:mb-10 max-w-lg leading-relaxed">
              日本人同士だから伝わる。本人確認済みの信頼できるシッターのみ掲載。
            </p>
            
            <Link 
              to="/sitters"
              className="group relative inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-[#81D8D0] text-white text-base sm:text-lg font-bold rounded-full shadow-lg shadow-[#81D8D0]/40 hover:bg-[#9fccc3] hover:shadow-xl hover:shadow-[#81D8D0]/50 hover:-translate-y-1 active:translate-y-0 transition-all duration-200 whitespace-nowrap mb-4"
            >
              {/* 光るアニメーション */}
              <span className="absolute inset-0 rounded-full overflow-hidden">
                <span className="absolute -inset-x-full top-0 h-full w-1/2 bg-white/20 skew-x-[-20deg] animate-[shimmer_2.5s_infinite]"></span>
              </span>
              <i className="ri-search-line text-xl sm:text-2xl"></i>
              近くのシッターを探す
              <i className="ri-arrow-right-line text-xl sm:text-2xl group-hover:translate-x-1 transition-transform duration-200"></i>
            </Link>

            {/* 安心保証バッジ */}
            <div className="mb-6 sm:mb-8">
              <Link
                to="/guarantee"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white/70 border border-[#81D8D0]/40 rounded-full hover:bg-white transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-sm text-[#81D8D0]"></i>
                </div>
                <span className="text-xs font-medium text-[#1B2A4A]/70">全ての予約に安心保証が含まれています</span>
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs text-[#81D8D0]"></i>
                </div>
              </Link>
            </div>

            {/* 社会的証明バッジ */}
            <div className="flex items-center gap-2 mb-8 sm:mb-10 md:mb-12">
              <div className="flex -space-x-2">
                <img src="https://public.readdy.ai/ai/img_res/0f6a4cd515c62ea5a59104ce03053cd6.jpg" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover object-top" />
                <img src="https://readdy.ai/api/search-image?query=japanese%20man%20smiling%20portrait%20close%20up%20soft%20background%20minimal&width=40&height=40&seq=badge2&orientation=squarish" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover object-top" />
                <img src="https://readdy.ai/api/search-image?query=japanese%20woman%20portrait%20close%20up%20warm%20background%20minimal&width=40&height=40&seq=badge3&orientation=squarish" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover object-top" />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                </div>
                <span className="text-xs sm:text-sm text-[#1B2A4A]/70 font-medium">200名以上が利用中</span>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-2xl shadow-xl p-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl">
              <div className="flex-1 flex items-center gap-2 px-4 sm:border-r border-[#D4E8EC]">
                <i className="ri-map-pin-line text-xl text-[#81D8D0]"></i>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full py-3 text-sm text-[#1B2A4A] bg-transparent outline-none cursor-pointer"
                >
                  <option value="">都市を選択</option>
                  {citiesByCountry.map(group => (
                    <optgroup key={group.country} label={group.country}>
                      {group.cities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="flex-1 flex items-center gap-2 px-4">
                <i className="ri-service-line text-xl text-[#81D8D0]"></i>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full py-3 text-sm text-[#1B2A4A] bg-transparent outline-none cursor-pointer"
                >
                  <option value="">サービスを選択</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleSearch}
                className="px-6 sm:px-8 py-3 bg-[#81D8D0] text-white rounded-xl hover:bg-[#9fccc3] transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12">
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-[#81D8D0] rounded-full flex items-center justify-center">
                <i className="ri-chat-3-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-3 sm:mb-4">100%日本語対応</h3>
              <p className="text-sm text-[#1B2A4A]/60 leading-relaxed">
                すべてのシッターが日本語ネイティブ。言葉の壁なく安心してコミュニケーション
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-[#81D8D0] rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-3 sm:mb-4">本人確認済みシッター</h3>
              <p className="text-sm text-[#1B2A4A]/60 leading-relaxed">
                日本人同士だから伝わる。本人確認済みの信頼できるシッターのみ掲載。
              </p>
            </div>
            <div className="text-center sm:col-span-2 md:col-span-1">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-[#81D8D0] rounded-full flex items-center justify-center">
                <i className="ri-shield-star-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-3 sm:mb-4">安心の保証制度</h3>
              <p className="text-sm text-[#1B2A4A]/60 leading-relaxed">
                プラットフォーム内予約で万が一に備える安心保証
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* How It Works */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#EEF4F7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <p className="text-xs font-semibold text-[#81D8D0] tracking-widest uppercase mb-3 sm:mb-4">HOW IT WORKS</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B2A4A]">3ステップで簡単予約</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-shadow relative">
              <div className="text-5xl sm:text-6xl md:text-7xl font-black text-[#81D8D0]/20 mb-3 sm:mb-4">1</div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1B2A4A] mb-3 sm:mb-4">シッターを検索</h3>
              <p className="text-sm sm:text-base text-[#1B2A4A]/60 leading-relaxed mb-4 sm:mb-6">
                都市とサービスで絞り込み。あなたのペットに最適なシッターを見つけましょう
              </p>
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
                <i className="ri-search-2-line text-3xl sm:text-4xl text-[#81D8D0]"></i>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-shadow relative">
              <div className="text-5xl sm:text-6xl md:text-7xl font-black text-[#81D8D0]/20 mb-3 sm:mb-4">2</div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1B2A4A] mb-3 sm:mb-4">チャットで相談</h3>
              <p className="text-sm sm:text-base text-[#1B2A4A]/60 leading-relaxed mb-4 sm:mb-6">
                日本語で安心コミュニケーション。ペットの性格や注意点を詳しく伝えられます
              </p>
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
                <i className="ri-message-3-line text-3xl sm:text-4xl text-[#81D8D0]"></i>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-shadow relative sm:col-span-2 md:col-span-1">
              <div className="text-5xl sm:text-6xl md:text-7xl font-black text-[#81D8D0]/20 mb-3 sm:mb-4">3</div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1B2A4A] mb-3 sm:mb-4">予約完了</h3>
              <p className="text-sm sm:text-base text-[#1B2A4A]/60 leading-relaxed mb-4 sm:mb-6">
                プラットフォーム内で安全に予約。安心してペットを預けられます
              </p>
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
                <i className="ri-calendar-check-line text-3xl sm:text-4xl text-[#81D8D0]"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Sitters */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B2A4A]">注目のシッター</h2>
            <Link
              to="/sitters"
              className="flex items-center gap-2 text-xs sm:text-sm font-medium text-[#81D8D0] hover:gap-3 transition-all whitespace-nowrap"
            >
              すべて見る
              <i className="ri-arrow-right-line text-base sm:text-lg"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {featuredSitters.slice(0, 3).map(sitter => (
              <div
                key={sitter.id}
                className="bg-[#EEF4F7] rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all group flex flex-col"
              >
                <Link to={`/sitters/${sitter.id}`} className="block">
                  <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden">
                    <img
                      src={sitter.profileImage}
                      alt={sitter.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {sitter.verified && (
                      <div className="absolute top-4 left-4 px-3 py-1.5 bg-[#81D8D0] text-white text-xs font-medium rounded-full flex items-center gap-1.5">
                        <i className="ri-shield-check-fill"></i>
                        本人確認済み
                      </div>
                    )}
                  </div>
                  <div className="p-5 sm:p-6 pb-3">
                    <h3 className="text-base sm:text-lg font-bold text-[#1B2A4A] mb-2">{sitter.name}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-[#1B2A4A]/60 mb-3">
                      <i className="ri-map-pin-line text-[#81D8D0]"></i>
                      {sitter.city}, {sitter.country}
                    </div>
                    <p className="text-sm text-[#1B2A4A]/60 mb-3 line-clamp-2 leading-relaxed">
                      {sitter.introduction}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-sm sm:text-base font-bold text-[#81D8D0]">
                        €{sitter.pricePerVisit}〜
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <i className="ri-star-fill text-yellow-400"></i>
                        <span className="font-semibold text-[#1B2A4A]">{sitter.rating}</span>
                        <span className="text-[#1B2A4A]/60">({sitter.reviewCount})</span>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 mt-auto pt-3 border-t border-[#D4E8EC]/60 flex gap-2">
                  <Link
                    to={`/sitters/${sitter.id}`}
                    className="flex-1 py-2.5 text-center text-sm font-semibold text-[#81D8D0] border border-[#81D8D0] rounded-xl hover:bg-[#EEF4F7] transition-colors whitespace-nowrap"
                  >
                    プロフィール
                  </Link>
                  <Link
                    to="/booking"
                    className="flex-1 py-2.5 text-center text-sm font-bold text-white bg-[#81D8D0] rounded-xl hover:bg-[#1B2A4A] transition-colors whitespace-nowrap"
                  >
                    今すぐ予約
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      {/* Sitter Recruit Section */}
      <SitterRecruitSection />

      {/* Services Overview */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#EEF4F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className="md:col-span-2">
              <p className="text-xs font-semibold text-[#81D8D0] tracking-widest uppercase mb-3 sm:mb-4">SERVICES</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B2A4A] mb-4 sm:mb-6 leading-tight">
                あなたのペットに<br />合わせたケア
              </h2>
              <p className="text-sm sm:text-base text-[#1B2A4A]/60 leading-relaxed">
                散歩から宿泊まで、様々なサービスをご用意。ペットの性格やニーズに合わせて最適なケアを提供します。
              </p>
            </div>

            <div className="md:col-span-3 space-y-4 sm:space-y-6">
              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-shadow flex items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#81D8D0] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-walk-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-2">犬の散歩</h3>
                  <p className="text-xs sm:text-sm text-[#1B2A4A]/60">信頼できるドッグウォーカーが愛犬をお世話。<br className="hidden sm:block" />散歩後には写真と最新情報をお届けします</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-shadow flex items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#81D8D0] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-heart-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-2">訪問ケア</h3>
                  <p className="text-xs sm:text-sm text-[#1B2A4A]/60">ご自宅でのお世話。餌やり、遊び、トイレ掃除など</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-shadow flex items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#81D8D0] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-hotel-bed-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-2">お泊まり</h3>
                  <p className="text-xs sm:text-sm text-[#1B2A4A]/60">シッター宅でのお預かり。24時間安心ケア</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-lg transition-shadow flex items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#81D8D0] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-taxi-line text-2xl sm:text-3xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#1B2A4A] mb-2">タクシー送迎</h3>
                  <p className="text-xs sm:text-sm text-[#1B2A4A]/60">車を持っていない方向けに動物病院などへの送迎サービス</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#D4F0ED]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
            <i className="ri-shield-star-line text-5xl sm:text-6xl text-[#81D8D0]"></i>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-4 sm:mb-5">PawBridge安心保証</h2>
          <p className="text-sm sm:text-base text-[#1B2A4A]/70 mb-6 sm:mb-8 leading-relaxed">
            プラットフォーム内予約のみ保証対象となります。<br />
            万が一のトラブルにも対応いたします。
          </p>
          <Link to="/guarantee" className="inline-block px-6 sm:px-8 py-2.5 sm:py-3 border-2 border-[#1B2A4A] text-[#1B2A4A] text-sm sm:text-base rounded-full hover:bg-[#81D8D0] hover:border-[#81D8D0] hover:text-white transition-all whitespace-nowrap cursor-pointer">
            詳しく見る
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Final CTA */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#EEF4F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B2A4A] mb-6 sm:mb-8">今すぐシッターを探す</h2>
          <Link 
            to="/sitters"
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-[#81D8D0] text-white text-base sm:text-lg font-semibold rounded-full shadow-lg shadow-[#81D8D0]/40 hover:bg-[#1B2A4A] transition-all hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap"
          >
            シッター検索を始める
            <i className="ri-arrow-right-line text-xl sm:text-2xl"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#81D8D0] text-white pt-12 sm:pt-16 md:pt-20 pb-8 sm:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-10 md:mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg" 
                alt="PawBridge" 
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain mb-4 sm:mb-5 brightness-0"
              />
              <p className="text-lg sm:text-xl font-bold text-white leading-relaxed">
                海外在住日本人の為の<br />ペットシッターコミュニティ
              </p>
            </div>

            <div>
              <h4 className="text-base font-semibold uppercase mb-4 sm:mb-6">サービス</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li><Link to="/sitters" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">シッター検索<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/sitter-registration" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">シッター登録<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/booking" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">料金<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold uppercase mb-4 sm:mb-6">会社情報</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li><Link to="/about" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">PawBridgeについて<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/contact" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">お問い合わせ<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/terms" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">PawBridge 利用規約<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/sitter-terms" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">PawBridge シッター利用規約<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/guarantee" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">PawBridge 安心保証規約<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
                <li><Link to="/privacy" className="relative text-base text-white/80 hover:text-white transition-colors group inline-block">プライバシーポリシー<span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span></Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-white/30 text-center">
            <p className="text-sm text-white/70">© 2025 PawBridge. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}